import { useState, useEffect, useRef, useMemo } from 'react';
import { writeOrgFile, deleteOrgFile, listOrgCollection, readOrgFile } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { callWorker } from '../../utils/workerClient';
import { areEquivalent } from '../../utils/legalEquivalents';
import { CRITICALITY_LEVELS, CUSTOMER_STATUSES, CUSTOMER_TYPES, GDPR_ROLES, DATA_CATEGORIES } from '../../utils/enums';
import { COUNTRIES } from '../../utils/countries';
import { SECTORS } from '../../utils/sectors';
import { SUBREGIONS } from '../../utils/subregions';
import AISuggestButton from '../shared/AISuggestButton';
import PeopleSelector from '../shared/PeopleSelector';
import TagInput from '../shared/TagInput';
import EvidenceSelector from '../shared/EvidenceSelector';
import ConfirmDialog from '../shared/ConfirmDialog';
import SaveErrorDialog from '../shared/SaveErrorDialog';
import InfoBadge from '../shared/InfoBadge';
import { BG_CONTENT, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_DEFAULT, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, BG_SURFACE, BG_SHELL, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_SECTION_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, STATUS_INFO, STATUS_GOOD, STATUS_WARN, STATUS_BAD } from '../../theme';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    titleCreate:          'Nuevo cliente',
    titleEdit:            'Editar cliente',
    titleView:            'Ver cliente',
    banner:               "Este registro complementa la categoría agregada 'Clientes' del módulo Contexto. Mantén las partes interesadas genéricas allí; este módulo es para clientes individuales con detalle jurisdiccional.",
    s1:                   'Identificación',
    s2:                   'Naturaleza de la relación',
    s3:                   'Sites del cliente',
    s4:                   'Datos del cliente que procesas',
    s5:                   'Rol GDPR',
    s6:                   'Compromisos contractuales',
    s7:                   'Acuerdos firmados',
    s8:                   'Criticidad',
    s9:                   'Contactos del cliente',
    s10:                  'Ciclo de vida',
    s11:                  'Notas',
    s12:                  'Sugerencias legales',
    s12ConsentMsg:        'Activa el procesamiento con IA en Configuración para usar esta función.',
    s12NoSitesMsg:        'Añade al menos un site con país especificado para generar sugerencias.',
    s12AiError:           'No se pudo obtener sugerencia. Inténtalo de nuevo.',
    s12AiErrorOverloaded: 'Servidores ocupados, inténtalo de nuevo en un momento.',
    s12SuggestionsHeader: 'Obligaciones sugeridas',
    s12NotSavedWarn:      'Guarda primero el cliente para vincular obligaciones legales o crear nuevas entradas. Mientras tanto, puedes ver las sugerencias pero las acciones quedarán bloqueadas.',
    s12TooltipNotSaved:   'Guarda el cliente para activar esta acción',
    s12CountCovered:      '{n} cubiertas',
    s12CountLinkable:     '{n} por vincular',
    s12CountMissing:      '{n} por crear',
    s12CountsSeparator:   ' · ',
    s12StateCovered:      'Ya en Registro Legal · cliente vinculado · {ref}',
    s12StateLinkable:     'En Registro Legal · este cliente no está vinculado · {ref}',
    s12StateMissing:      'No existe en Registro Legal',
    s12LinkBtn:           'Vincular cliente',
    s12CreateBtn:         'Crear en Registro Legal',
    s12BulkSummary:       '{n} por crear y {m} por vincular.',
    s12LinkAll:           'Vincular todos ({n})',
    s12CreateAll:         'Crear todos ({n})',
    s12LinkAllConfirm:    'Vas a vincular este cliente a {n} entradas existentes del Registro Legal. ¿Continuar?',
    s12CreateAllConfirm:  'Vas a crear {n} nuevas entradas en el Registro Legal. ¿Continuar?',
    s12LinkConfirmLabel:  'Vincular',
    s12CreateConfirmLabel:'Crear',
    s12CancelLabel:       'Cancelar',
    s12PillLinked:        'vinculado ✓',
    s12PillCreated:       'creado ✓',
    s12LinkError:         'No se pudo vincular el cliente a esta entrada. Inténtalo de nuevo.',
    s12CreateError:       'No se pudo crear la entrada en el Registro Legal. Inténtalo de nuevo.',
    s12Disclaimer:        'Lista orientativa. No sustituye un análisis legal profesional.',
    fRef:                 'Referencia',
    fName:                'Nombre',
    fLegalName:           'Nombre legal',
    fSector:              'Sector',
    fCustomerType:        'Tipo de cliente',
    fServicesProvided:    'Servicios proporcionados',
    fSiteName:            'Nombre del site',
    fCity:                'Ciudad',
    fCountry:             'País',
    fSubregion:           'Región / subdivisión',
    freeText:             '(campo libre)',
    fUnits:               'Unidades / áreas',
    fProcessesPersonalData: 'Procesas datos personales de este cliente',
    fDataCategories:      'Categorías de datos',
    fDataStorageLocations:'Ubicaciones de almacenamiento de datos',
    fGdprRole:            'Rol GDPR',
    fIncidentSla:         'SLA notificación de incidentes (horas)',
    fRto:                 'RTO (horas)',
    fRpo:                 'RPO (horas)',
    fAvailabilitySla:     'SLA de disponibilidad',
    fRightToAudit:        'Derecho de auditoría',
    fRequiredCertifications: 'Certificaciones requeridas',
    fDpaSigned:           'DPA firmado',
    fNdaSigned:           'NDA firmado',
    fSecurityAddendumSigned: 'Anexo de seguridad firmado',
    fDpaEvidence:         'Evidencia del DPA',
    fNdaEvidence:         'Evidencia del NDA',
    fSecurityAddendumEvidence: 'Evidencia del anexo de seguridad',
    fCriticality:         'Criticidad',
    fTechnicalContact:    'Contacto técnico',
    fLegalContact:        'Contacto legal',
    fIncidentNotificationContact: 'Contacto para notificación de incidentes',
    fStatus:              'Estado',
    fNextReviewDate:      'Próxima revisión',
    fRelationshipStart:   'Inicio de la relación',
    fContractEnd:         'Fin del contrato',
    fNotes:               'Notas',
    addSite:              '+ Añadir site',
    removeSite:           'Eliminar site',
    confirmRemoveSite:    '¿Eliminar este site del cliente?',
    jurisdictionsDerived: 'Jurisdicciones derivadas: ',
    emptySites:           'Sin sites registrados',
    sitesCounter:         '{n} sites · {m} países · {k} regiones',
    optional:             '(opcional)',
    btnSave:              'Guardar',
    btnCancel:            'Cancelar',
    btnDelete:            'Eliminar cliente',
    btnConfirmDelete:     '¿Confirmar eliminación?',
    saving:               'Guardando…',
    saveOk:               '✓ Guardado',
    errorRequired:        'Campo obligatorio',
    errorSave:            'Error al guardar. Inténtalo de nuevo.',
    errorDelete:          'Error al eliminar.',
    errorSiteRequired:    'Cada site requiere nombre y país.',
    required:             '*',
    requiredLegend:       '* Campos obligatorios',
    servicePlaceholder:   'Añadir servicio y pulsar Enter…',
    unitsPlaceholder:     'Añadir unidad/área y pulsar Enter…',
    dataCategoriesPlaceholder: 'p.ej. PII general, PII special, Health, Financial…',
    storageLocationsPlaceholder: 'p.ej. España, Alemania, EE. UU.',
    certificationsPlaceholder: 'Añadir certificación y pulsar Enter…',
    generating:           'Generando…',
    unsavedLabel:         'Cambios sin guardar',
    ttGdprRole:           'Rol GDPR de tu organización respecto a los datos personales de este cliente: Controller (tomas las decisiones), Processor (los tratas por su cuenta), Joint controller (decisión compartida) o Not applicable.',
    customerTypeLabels: {
      'Corporate':     'Corporativo',
      'SMB':           'PYME',
      'Public sector': 'Sector público',
      'Academic':      'Académico',
      'Other':         'Otro',
    },
    criticalityLabels: {
      Critical: 'Crítico',
      High:     'Alta',
      Medium:   'Media',
      Low:      'Baja',
    },
    statusLabels: {
      'Prospective': 'Prospecto',
      'Active':      'Activo',
      'Paused':      'Pausado',
      'Terminated':  'Terminado',
    },
    gdprRoleLabels: {
      'Controller':       'Responsable (Controller)',
      'Processor':        'Encargado (Processor)',
      'Joint controller': 'Corresponsable',
      'Not applicable':   'No aplica',
    },
  },
  en: {
    titleCreate:          'New customer',
    titleEdit:            'Edit customer',
    titleView:            'View customer',
    banner:               "This record complements the aggregated 'Customers' category in the Context module. Keep generic interested parties there; this module is for individual customers with jurisdictional detail.",
    s1:                   'Identification',
    s2:                   'Nature of the relationship',
    s3:                   'Customer sites',
    s4:                   'Customer data you process',
    s5:                   'GDPR role',
    s6:                   'Contractual commitments',
    s7:                   'Signed agreements',
    s8:                   'Criticality',
    s9:                   'Customer contacts',
    s10:                  'Lifecycle',
    s11:                  'Notes',
    s12:                  'Legal suggestions',
    s12ConsentMsg:        'Enable AI processing in Settings to use this feature.',
    s12NoSitesMsg:        'Add at least one site with a country to generate suggestions.',
    s12AiError:           'Could not get suggestion. Please try again.',
    s12AiErrorOverloaded: 'Servers are busy, please try again in a moment.',
    s12SuggestionsHeader: 'Suggested obligations',
    s12NotSavedWarn:      'Save the customer first to link legal obligations or create new entries. In the meantime you can preview the suggestions, but actions remain blocked.',
    s12TooltipNotSaved:   'Save the customer to enable this action',
    s12CountCovered:      '{n} covered',
    s12CountLinkable:     '{n} to link',
    s12CountMissing:      '{n} to create',
    s12CountsSeparator:   ' · ',
    s12StateCovered:      'Already in Legal Register · customer linked · {ref}',
    s12StateLinkable:     'In Legal Register · this customer is not linked · {ref}',
    s12StateMissing:      'Not in Legal Register',
    s12LinkBtn:           'Link customer',
    s12CreateBtn:         'Create in Legal Register',
    s12BulkSummary:       '{n} to create and {m} to link.',
    s12LinkAll:           'Link all ({n})',
    s12CreateAll:         'Create all ({n})',
    s12LinkAllConfirm:    'You are about to link this customer to {n} existing Legal Register entries. Continue?',
    s12CreateAllConfirm:  'You are about to create {n} new Legal Register entries. Continue?',
    s12LinkConfirmLabel:  'Link',
    s12CreateConfirmLabel:'Create',
    s12CancelLabel:       'Cancel',
    s12PillLinked:        'linked ✓',
    s12PillCreated:       'created ✓',
    s12LinkError:         'Could not link the customer to this entry. Please try again.',
    s12CreateError:       'Could not create the Legal Register entry. Please try again.',
    s12Disclaimer:        'Orientative list. Does not replace a professional legal analysis.',
    fRef:                 'Reference',
    fName:                'Name',
    fLegalName:           'Legal name',
    fSector:              'Sector',
    fCustomerType:        'Customer type',
    fServicesProvided:    'Services provided',
    fSiteName:            'Site name',
    fCity:                'City',
    fCountry:             'Country',
    fSubregion:           'Region / subdivision',
    freeText:             '(free text)',
    fUnits:               'Units / areas',
    fProcessesPersonalData: 'You process personal data of this customer',
    fDataCategories:      'Data categories',
    fDataStorageLocations:'Data storage locations',
    fGdprRole:            'GDPR role',
    fIncidentSla:         'Incident notification SLA (hours)',
    fRto:                 'RTO (hours)',
    fRpo:                 'RPO (hours)',
    fAvailabilitySla:     'Availability SLA',
    fRightToAudit:        'Right to audit',
    fRequiredCertifications: 'Required certifications',
    fDpaSigned:           'DPA signed',
    fNdaSigned:           'NDA signed',
    fSecurityAddendumSigned: 'Security addendum signed',
    fDpaEvidence:         'DPA evidence',
    fNdaEvidence:         'NDA evidence',
    fSecurityAddendumEvidence: 'Security addendum evidence',
    fCriticality:         'Criticality',
    fTechnicalContact:    'Technical contact',
    fLegalContact:        'Legal contact',
    fIncidentNotificationContact: 'Incident notification contact',
    fStatus:              'Status',
    fNextReviewDate:      'Next review date',
    fRelationshipStart:   'Relationship start date',
    fContractEnd:         'Contract end date',
    fNotes:               'Notes',
    addSite:              '+ Add site',
    removeSite:           'Remove site',
    confirmRemoveSite:    'Remove this customer site?',
    jurisdictionsDerived: 'Derived jurisdictions: ',
    emptySites:           'No sites recorded',
    sitesCounter:         '{n} sites · {m} countries · {k} regions',
    optional:             '(optional)',
    btnSave:              'Save',
    btnCancel:            'Cancel',
    btnDelete:            'Delete customer',
    btnConfirmDelete:     'Confirm delete?',
    saving:               'Saving…',
    saveOk:               '✓ Saved',
    errorRequired:        'Required',
    errorSave:            'Save failed. Please try again.',
    errorDelete:          'Delete failed.',
    errorSiteRequired:    'Every site requires a name and a country.',
    required:             '*',
    requiredLegend:       '* Required fields',
    servicePlaceholder:   'Add service and press Enter…',
    unitsPlaceholder:     'Add unit/area and press Enter…',
    dataCategoriesPlaceholder: 'e.g. PII general, PII special, Health, Financial…',
    storageLocationsPlaceholder: 'e.g. Spain, Germany, United States',
    certificationsPlaceholder: 'Add certification and press Enter…',
    generating:           'Generating…',
    unsavedLabel:         'Unsaved changes',
    ttGdprRole:           "Your organization's GDPR role with respect to this customer's personal data: Controller (you make the decisions), Processor (you process them on their behalf), Joint controller (shared decision) or Not applicable.",
    customerTypeLabels: {
      'Corporate':     'Corporate',
      'SMB':           'SMB',
      'Public sector': 'Public sector',
      'Academic':      'Academic',
      'Other':         'Other',
    },
    criticalityLabels: {
      Critical: 'Critical',
      High:     'High',
      Medium:   'Medium',
      Low:      'Low',
    },
    statusLabels: {
      'Prospective': 'Prospective',
      'Active':      'Active',
      'Paused':      'Paused',
      'Terminated':  'Terminated',
    },
    gdprRoleLabels: {
      'Controller':       'Controller',
      'Processor':        'Processor',
      'Joint controller': 'Joint controller',
      'Not applicable':   'Not applicable',
    },
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function slugify(name) {
  return (name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

async function generateCustomerIds(orgId, name, refPrefix) {
  let customers = [];
  try { customers = await listOrgCollection(orgId, 'customers'); } catch { /* empty */ }

  // File id: cus-<slug-of-name> with -2, -3 collision suffix.
  const base = `cus-${slugify(name) || 'new'}`;
  const existingIds = new Set(customers.map((c) => c.id));
  let fileId = base;
  let n = 2;
  while (existingIds.has(fileId)) {
    fileId = `${base}-${n}`;
    n += 1;
  }

  // Ref: cus-NNN auto-incremented per org. Use refPrefix from profile when set.
  const prefix = (refPrefix || 'cus').toLowerCase();
  let maxSeq = 0;
  customers.forEach((c) => {
    if (c.ref && c.ref.toLowerCase().startsWith(prefix + '-')) {
      const n2 = parseInt(c.ref.slice(prefix.length + 1), 10);
      if (!isNaN(n2) && n2 > maxSeq) maxSeq = n2;
    }
  });
  const ref = `${prefix}-${String(maxSeq + 1).padStart(3, '0')}`;

  return { fileId, ref };
}

function emptySite() {
  return { siteName: '', city: '', country: '', subregion: '', units: [] };
}

// Classify each AI bullet against the org's existing Legal Register entries.
// Returns one entry per bullet: { text, state, matchedEntry, pendingAction, recentAction, error }.
// state ∈ 'covered' | 'linkable' | 'missing'. First match wins; empty strings never match.
function classifySuggestionsFn(bullets, lrEntries, currentCustomerId) {
  return (bullets || []).map((rawText) => {
    const text = (rawText || '').trim();
    let matched = null;
    if (text) {
      for (const e of lrEntries || []) {
        const title = e && typeof e.title === 'string' ? e.title : '';
        if (!title) continue;
        if (areEquivalent(text, title)) { matched = e; break; }
      }
    }
    let state;
    if (matched) {
      const trig = Array.isArray(matched.triggeringCustomerIds) ? matched.triggeringCustomerIds : [];
      state = (currentCustomerId && trig.includes(currentCustomerId)) ? 'covered' : 'linkable';
    } else {
      state = 'missing';
    }
    return { text, state, matchedEntry: matched, pendingAction: null, recentAction: null, error: null };
  });
}

// Build a fresh Legal Register {fileId, ref} pair scoped to orgId + prefix.
// Inlined intentionally from LegalRegisterView.genId — duplicating the small helper
// avoids a cross-module import (see CONSTRAINTS in the spec for this commit).
async function genLegalRegisterId(orgId, lrPrefix) {
  const now  = new Date();
  const year = now.getFullYear();
  const mm   = String(now.getMonth() + 1).padStart(2, '0');
  const dd   = String(now.getDate()).padStart(2, '0');
  const prefix = `${lrPrefix}-${year}-`;
  try {
    const items = await listOrgCollection(orgId, 'legal-register');
    let max = 0;
    items.forEach((e) => {
      if (e.ref && e.ref.startsWith(prefix)) {
        const n = parseInt(e.ref.slice(prefix.length), 10);
        if (!isNaN(n) && n > max) max = n;
      }
    });
    const seq = String(max + 1).padStart(3, '0');
    return { fileId: `lr-${year}${mm}${dd}-${seq}`, ref: `${prefix}${seq}` };
  } catch {
    return { fileId: `lr-${year}${mm}${dd}-001`, ref: `${prefix}001` };
  }
}

// ── INITIAL STATE ─────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: '',
  legalName: '',
  sector: '',
  customerType: 'Corporate',
  servicesProvided: [],
  sites: [],
  processesPersonalData: false,
  dataCategories: [],
  dataStorageLocations: [],
  gdprRole: 'Not applicable',
  incidentNotificationSlaHours: '',
  rtoHours: '',
  rpoHours: '',
  availabilitySla: '',
  rightToAudit: false,
  requiredCertifications: [],
  dpaSigned: false,
  dpaEvidenceId: null,
  ndaSigned: false,
  ndaEvidenceId: null,
  securityAddendumSigned: false,
  securityAddendumEvidenceId: null,
  criticality: 'Medium',
  technicalContact: '',
  legalContact: '',
  incidentNotificationContact: '',
  status: 'Prospective',
  nextReviewDate: '',
  relationshipStartDate: '',
  contractEndDate: '',
  notes: '',
};

function customerToForm(c) {
  return {
    name:                         c.name                          || '',
    legalName:                    c.legalName                     || '',
    sector:                       c.sector                        || '',
    customerType:                 CUSTOMER_TYPES.includes(c.customerType) ? c.customerType : 'Other',
    servicesProvided:             Array.isArray(c.servicesProvided) ? c.servicesProvided : [],
    sites:                        Array.isArray(c.sites)
      ? c.sites.map((s) => ({
          siteName:  s.siteName  || '',
          city:      s.city      || '',
          country:   s.country   || '',
          subregion: s.subregion || '',
          units:     Array.isArray(s.units) ? s.units : [],
        }))
      : [],
    processesPersonalData:        !!c.processesPersonalData,
    dataCategories:               Array.isArray(c.dataCategories) ? c.dataCategories : [],
    dataStorageLocations:         Array.isArray(c.dataStorageLocations) ? c.dataStorageLocations : [],
    gdprRole:                     GDPR_ROLES.includes(c.gdprRole) ? c.gdprRole : 'Not applicable',
    incidentNotificationSlaHours: c.incidentNotificationSlaHours != null ? String(c.incidentNotificationSlaHours) : '',
    rtoHours:                     c.rtoHours != null ? String(c.rtoHours) : '',
    rpoHours:                     c.rpoHours != null ? String(c.rpoHours) : '',
    availabilitySla:              c.availabilitySla              || '',
    rightToAudit:                 !!c.rightToAudit,
    requiredCertifications:       Array.isArray(c.requiredCertifications) ? c.requiredCertifications : [],
    dpaSigned:                    !!c.dpaSigned,
    dpaEvidenceId:                c.dpaEvidenceId || null,
    ndaSigned:                    !!c.ndaSigned,
    ndaEvidenceId:                c.ndaEvidenceId || null,
    securityAddendumSigned:       !!c.securityAddendumSigned,
    securityAddendumEvidenceId:   c.securityAddendumEvidenceId || null,
    criticality:                  CRITICALITY_LEVELS.includes(c.criticality) ? c.criticality : 'Medium',
    technicalContact:             c.technicalContact             || '',
    legalContact:                 c.legalContact                 || '',
    incidentNotificationContact:  c.incidentNotificationContact  || '',
    status:                       CUSTOMER_STATUSES.includes(c.status) ? c.status : 'Prospective',
    nextReviewDate:               c.nextReviewDate               || '',
    relationshipStartDate:        c.relationshipStartDate        || '',
    contractEndDate:              c.contractEndDate              || '',
    notes:                        c.notes                        || '',
  };
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const labelSt = {
  ...FONT_SMALL,
  display: 'block', color: TEXT_MUTED,
  letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.3rem',
};
const inputSt = (hasErr) => ({
  ...FONT_BODY,
  width: '100%', padding: '0.55rem 0.7rem',
  border: `1px solid ${hasErr ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6, background: SURFACE_WHITE,
  color: TEXT_HEADING, outline: 'none', boxSizing: 'border-box',
});
const textareaSt = (hasErr) => ({ ...inputSt(hasErr), resize: 'vertical', minHeight: 72 });
const selectSt = { ...inputSt(false), cursor: 'pointer' };
const sectionHeading = {
  ...FONT_BODY_STRONG,
  color: TEXT_HEADING,
  margin: '0 0 1rem', paddingBottom: '0.5rem',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
};
const fieldWrap = { marginBottom: '1rem' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' };

function ErrMsg({ msg }) {
  if (!msg) return null;
  return (
    <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0.2rem 0 0' }}>
      {msg}
    </p>
  );
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function CustomerForm({ customer, mode, org, lang, onSave, onCancel, onFormChange, saveRequestTick, dirty, onNavigate }) {
  const t      = T[lang] || T.en;
  const isEdit = !!customer;
  const isView = mode === 'view';
  const [currentMode, setCurrentMode] = useState(mode || (isEdit ? 'edit' : 'create'));
  const [savedRecord, setSavedRecord] = useState(isEdit ? customer : null);
  const hasChangedRef = useRef(false);

  const [form, setForm] = useState(() => isEdit ? customerToForm(customer) : { ...EMPTY_FORM });
  const [fileId, setFileId]           = useState(isEdit ? customer.id  : null);
  const [ref, setRef]                 = useState(isEdit ? customer.ref : null);
  const [errors, setErrors]           = useState({});
  const [siteErrors, setSiteErrors]   = useState([]);
  const [saveState, setSaveState]     = useState('idle');
  const [saveErrorOpen, setSaveErrorOpen] = useState(false);
  const [confirmState, setConfirmState]   = useState(null);
  const [deleteError, setDeleteError]     = useState(null);

  // ── AI legal suggestions (Section 12) ─────────────────────────────────────
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiError,      setAiError]      = useState(null);
  const [aiBullets,    setAiBullets]    = useState([]);
  const [aiFallback,   setAiFallback]   = useState('');

  // Cross-reference between AI bullets and existing Legal Register entries.
  // legalRegisterEntries is lazily loaded after the first successful AI generation
  // and refreshed after every per-bullet or bulk action.
  const [legalRegisterEntries, setLegalRegisterEntries] = useState([]);
  const [classifiedSuggestions, setClassifiedSuggestions] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const pillTimersRef = useRef([]);

  // Generate IDs on create — depends on form.name so cus-<slug-of-name> stays
  // current as the user types.
  useEffect(() => {
    if (isEdit) return;
    const refPrefix = (org.refPrefixes && org.refPrefixes.customer) || 'cus';
    generateCustomerIds(org.id, form.name, refPrefix)
      .then(({ fileId: fid, ref: r }) => { setFileId(fid); setRef(r); })
      .catch(() => { setFileId(`cus-${slugify(form.name) || 'new'}`); setRef(`cus-001`); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name]);

  const markChange = () => {
    if (!isView && !hasChangedRef.current && onFormChange) {
      hasChangedRef.current = true;
      onFormChange();
    }
  };

  const sf  = (field) => (val) => { markChange(); setForm((p) => ({ ...p, [field]: val })); };
  const hic = (field) => (e)   => sf(field)(e.target.value);

  // ── Site helpers ──────────────────────────────────────────────────────────
  const updateSite = (idx, patch) => {
    markChange();
    setForm((p) => ({
      ...p,
      sites: p.sites.map((s, i) => i === idx ? { ...s, ...patch } : s),
    }));
  };
  const addSite = () => {
    markChange();
    setForm((p) => ({ ...p, sites: [...p.sites, emptySite()] }));
  };
  const requestRemoveSite = (idx) => {
    setConfirmState({
      message: t.confirmRemoveSite,
      confirmLabel: t.removeSite,
      onConfirm: () => {
        setConfirmState(null);
        markChange();
        setForm((p) => ({ ...p, sites: p.sites.filter((_, i) => i !== idx) }));
      },
    });
  };

  // ── Derived jurisdictions ─────────────────────────────────────────────────
  const resolveCountryLabel = (code) => {
    const c = COUNTRIES.find((x) => x.code === code);
    return c ? (lang === 'es' ? c.nameEs : c.nameEn) : code;
  };
  const resolveSubregionLabel = (countryCode, code) => {
    const list = SUBREGIONS[countryCode];
    if (Array.isArray(list)) {
      const item = list.find((x) => x.code === code);
      if (item) return lang === 'es' ? item.nameEs : item.nameEn;
    }
    return code;
  };
  const derivedJurisdictions = (() => {
    const countries = new Set();
    const regions = new Set();
    for (const s of form.sites) {
      if (s.country) countries.add(s.country);
      if (s.subregion) regions.add(`${s.country}::${s.subregion}`);
    }
    const countryLabels = [...countries].map(resolveCountryLabel);
    const regionLabels = [...regions].map((key) => {
      const [cc, code] = key.split('::');
      return resolveSubregionLabel(cc, code);
    });
    return { countries, regions, labels: [...countryLabels, ...regionLabels] };
  })();
  const siteCount = form.sites.length;
  const sitesCounter = t.sitesCounter
    .replace('{n}', siteCount)
    .replace('{m}', derivedJurisdictions.countries.size)
    .replace('{k}', derivedJurisdictions.regions.size);

  // ── Sanitized AI payload (Section 12) ─────────────────────────────────────
  // Strict allowlist: only the fields listed below may flow into the prompt
  // string. See FIELD_NOTES.md "AI sanitization invariant" for the binding
  // contract.
  const sanitizedAiPayload = useMemo(() => ({
    sitesJurisdictions: form.sites
      .filter((s) => !!s.country)
      .map((s) => ({ country: s.country, subregion: s.subregion || '' })),
    sector:                form.sector || '',
    customerType:          form.customerType,
    processesPersonalData: !!form.processesPersonalData,
    dataCategories:        Array.isArray(form.dataCategories) ? form.dataCategories : [],
    gdprRole:              form.gdprRole,
    regions:               Array.isArray(org.regions) ? org.regions : [],
  }), [form.sites, form.sector, form.customerType, form.processesPersonalData, form.dataCategories, form.gdprRole, org.regions]);

  const handleGenerateSuggestions = async () => {
    if (!org.aiProcessingConsent) return;
    if (sanitizedAiPayload.sitesJurisdictions.length === 0) return;
    setAiSuggesting(true);
    setAiError(null);
    setAiBullets([]);
    setAiFallback('');

    const langName        = org.language === 'es' ? 'Spanish' : 'English';

    const jurisdictionsLines = sanitizedAiPayload.sitesJurisdictions
      .map((j) => j.subregion ? `${j.country} / ${j.subregion}` : j.country)
      .join('; ');
    const dataCategoriesStr = sanitizedAiPayload.dataCategories.length > 0
      ? sanitizedAiPayload.dataCategories.join(', ')
      : 'None specified';

    const prompt = `You are an expert in information security, cybersecurity and data privacy regulatory compliance.
The application you are assisting manages an Information Security Management System (ISMS) per ISO/IEC 27001:2022. Recommendations must be scoped EXCLUSIVELY to legally binding obligations whose subject-matter domain is information security, cybersecurity, data protection or privacy.

IN SCOPE — recommend ONLY legally binding instruments whose subject matter is:
- personal data protection and privacy (e.g. GDPR, LOPDGDD, CCPA, LGPD, BDSG, Argentina Ley 25.326)
- cybersecurity and information security (e.g. NIS2, DORA, EU Cyber Resilience Act, NY SHIELD Act, Texas Business & Commerce §521, CIRCIA, China Cybersecurity Law)
- security incident and personal data breach notification (any jurisdiction)
- sector-specific privacy or information security regimes (e.g. HIPAA for healthcare, GLBA for finance, FERPA for education, PCI DSS as a binding contractual framework where the inputs imply payment card data)
- cybersecurity of critical infrastructure
- electronic communications privacy, retention of communications and logs, lawful interception, and workplace monitoring of personal data
- export controls specific to cybersecurity or cryptographic technology (e.g. Wassenaar Arrangement, EAR ECCN 5A002, dual-use cyber items)
- national security information classification regimes that govern handling of restricted information (e.g. NIST SP 800-171 for US defense contractors, Spanish Ley de Secretos Oficiales)

OUT OF SCOPE — do NOT recommend any of the following, even when the input sector or jurisdiction would normally trigger them:
- process safety, occupational health and safety (e.g. OSHA PSM, Seveso III, ATEX, MAH regulations)
- environmental protection (e.g. Clean Air Act, CERCLA, REACH, water and soil regulations)
- general labor and employment law (consider it only where it specifically governs employee personal data or workplace digital surveillance)
- tax, corporate and securities law (consider only specifically cyber-related securities disclosure rules such as SEC Item 1.05 on material cybersecurity incidents)
- general commercial or contract law (PCI DSS is the only contractual framework in scope)
- building codes, transport safety and product safety (consider only specifically cyber-related rules such as TSA pipeline cybersecurity directives)
- voluntary standards and frameworks (ISO 27001, ISO 27002, ISO 27701, SOC 2, NIST CSF, NIST 800-53, COBIT, CIS Controls, ENISA guidance, ISF, or any similar)

Treat the IN SCOPE and OUT OF SCOPE lists as exhaustive in their respective directions. If an obligation does not clearly fall into an IN SCOPE category, omit it.

Now identify legally binding obligations within the IN SCOPE list that are triggered by THIS CUSTOMER's jurisdictions, sector, services consumed, or data profile, given the inputs below.
Customer jurisdictions (country / subregion of each site): ${jurisdictionsLines}
Customer sector: ${sanitizedAiPayload.sector || 'Not specified'}
Customer type: ${sanitizedAiPayload.customerType}
Processes personal data: ${sanitizedAiPayload.processesPersonalData ? 'yes' : 'no'}
Data categories: ${dataCategoriesStr}
GDPR role of the organization with respect to this customer's personal data: ${sanitizedAiPayload.gdprRole}
Do not include any obligation that depends on facts not stated in the inputs above.
Limit the recommendation to at most ten obligations.
Do not provide per-obligation justifications, parenthetical explanations, citations, or examples of regulated activities.
Do not annotate any bullet item with parentheticals, status notes, or cross-references such as '(already listed)', '(skip)', '(see below)', '(ya cubierta)', '(ya registrada)', or any equivalent. Each bullet contains only the obligation name.
List ALL legally binding obligations within the IN SCOPE list that apply based on the inputs above. Do not omit an obligation because it is widely-known, common, default, or otherwise assumed in the customer's jurisdiction. If GDPR applies because the customer has sites in the EU or processes the personal data of EU residents, list it explicitly. If a US state breach-notification law applies because the customer has sites in that state, list it explicitly. If a sector-specific framework applies because the customer's sector triggers it (e.g. HIPAA for Healthcare, GLBA for Finance, FERPA for Education, PCI DSS where payment card data is processed), list it explicitly.
Return your response in exactly ONE of the two shapes below. Choose exactly ONE of the two shapes. Do not combine them. Do not introduce any other shape.
Shape A — at least one obligation: a bulleted list, one obligation per line, each line starting with "- " (a hyphen followed by a single space) and the obligation name with no annotation, parenthetical status note, or cross-reference of any kind. Up to ten lines. No surrounding sentence, no preamble, no trailing summary line, no per-item justification or parenthetical.
Shape B — no obligations apply: a single plain-text sentence stating that none of the IN SCOPE legally binding obligations apply to the customer based on the inputs given. The sentence must NOT contain any hyphen-prefixed line and must NOT enumerate any obligation.
Do not write a disclaimer; the application appends one.
Reply in ${langName}.`;

    const result = await callWorker({ prompt, maxTokens: 500 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiError(busy ? t.s12AiErrorOverloaded : t.s12AiError);
      setAiSuggesting(false);
      return;
    }
    const cleaned = (result.text || '')
      .replace(/```[a-z]*\n?/gi, '')
      .replace(/```/g, '')
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/`/g, '')
      .replace(/^[\s>]*•\s*/gm, '')
      .split('\n')
      .map((line) => line.replace(/\s+$/, ''))
      .join('\n')
      .trim();
    const bulletLines = cleaned
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => /^-\s/.test(line))
      .map((line) => line.replace(/^-\s+/, '').trim())
      .filter(Boolean);
    // Refresh the Legal Register entries cache so the classifier sees the latest
    // state on disk before classifying the new AI bullets. Failure here is non-fatal:
    // an empty entries list classifies every bullet as 'missing', which is a safe default.
    try {
      const items = await listOrgCollection(org.id, 'legal-register');
      setLegalRegisterEntries(Array.isArray(items) ? items : []);
    } catch {
      setLegalRegisterEntries([]);
    }
    if (bulletLines.length >= 2) {
      setAiBullets(bulletLines.slice(0, 10));
      setAiFallback('');
    } else {
      setAiBullets([]);
      setAiFallback(cleaned);
    }
    setAiSuggesting(false);
  };

  // Derive classifiedSuggestions whenever bullets, the Legal Register cache, or
  // the saved customer id change. Replacing the array (rather than merging) is the
  // correct behaviour on regeneration — pendingAction/recentAction/error never need
  // to survive a regeneration.
  useEffect(() => {
    const currentId = savedRecord ? savedRecord.id : null;
    setClassifiedSuggestions(classifySuggestionsFn(aiBullets, legalRegisterEntries, currentId));
  }, [aiBullets, legalRegisterEntries, savedRecord]);

  // Clear scheduled pill-clear timers on unmount to avoid setState on an
  // unmounted component.
  useEffect(() => () => {
    pillTimersRef.current.forEach((id) => clearTimeout(id));
    pillTimersRef.current = [];
  }, []);

  // ── Per-bullet and bulk Legal Register actions (Section 12) ───────────────
  // Reload the Legal Register entries cache after a successful per-bullet or bulk
  // action so subsequent classifications reflect the latest disk state.
  const reloadLegalRegisterEntries = async () => {
    try {
      const items = await listOrgCollection(org.id, 'legal-register');
      setLegalRegisterEntries(Array.isArray(items) ? items : []);
    } catch {
      // Keep the prior cache on failure rather than clobbering it.
    }
  };

  // Schedule a pill clear after 3s and track the timer id for cleanup on unmount.
  const schedulePillClear = (index) => {
    const id = setTimeout(() => {
      setClassifiedSuggestions((prev) => prev.map((c, i) => (i === index ? { ...c, recentAction: null } : c)));
    }, 3000);
    pillTimersRef.current.push(id);
  };

  const updateBullet = (index, patch) => {
    setClassifiedSuggestions((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  // Compute auto-derived jurisdiction for a new Legal Register entry:
  // exactly one unique non-empty country across all sites → use it; otherwise empty.
  const deriveSingleCountryOrEmpty = () => {
    const set = new Set(form.sites.map((s) => (s.country || '').trim()).filter(Boolean));
    return set.size === 1 ? [...set][0] : '';
  };

  // Linking the current customer to an existing Legal Register entry.
  const linkCustomerToEntry = async (index) => {
    if (!savedRecord) return;
    const bullet = classifiedSuggestions[index];
    if (!bullet || !bullet.matchedEntry) return;

    updateBullet(index, { pendingAction: 'linking', error: null });

    let existing;
    try {
      existing = await readOrgFile(org.id, `legal-register/${bullet.matchedEntry.id}.json`);
    } catch {
      updateBullet(index, { pendingAction: null, error: t.s12LinkError });
      return false;
    }

    const existingTriggers = Array.isArray(existing.triggeringCustomerIds) ? existing.triggeringCustomerIds : [];
    if (existingTriggers.includes(savedRecord.id)) {
      // Idempotency guard: already linked on disk; advance UI without writing.
      updateBullet(index, { state: 'covered', matchedEntry: existing, pendingAction: null, recentAction: 'linked', error: null });
      schedulePillClear(index);
      await reloadLegalRegisterEntries();
      return true;
    }

    const updated = {
      ...existing,
      triggeringCustomerIds: [...existingTriggers, savedRecord.id],
      updatedAt: new Date().toISOString(),
    };
    updated.changeLog = appendChangeLog(existing, updated, org.consultantPersonId || 'system');

    try {
      await writeOrgFile(org.id, `legal-register/${existing.id}.json`, updated);
    } catch {
      updateBullet(index, { pendingAction: null, error: t.s12LinkError });
      return false;
    }

    updateBullet(index, { state: 'covered', matchedEntry: updated, pendingAction: null, recentAction: 'linked', error: null });
    schedulePillClear(index);
    await reloadLegalRegisterEntries();
    return true;
  };

  // Creating a new Legal Register entry pre-populated from the bullet text.
  const createEntryFromBullet = async (index) => {
    if (!savedRecord) return;
    const bullet = classifiedSuggestions[index];
    if (!bullet) return;

    updateBullet(index, { pendingAction: 'creating', error: null });

    const lrPrefix = (org.refPrefixes && org.refPrefixes.legalRegister) || 'lrg';
    let fileId;
    let ref;
    try {
      ({ fileId, ref } = await genLegalRegisterId(org.id, lrPrefix));
    } catch {
      updateBullet(index, { pendingAction: null, error: t.s12CreateError });
      return false;
    }

    const now = new Date().toISOString();
    const jurisdictionAutoDerived = deriveSingleCountryOrEmpty();
    const newEntry = {
      id:                         fileId,
      orgId:                      org.id,
      ref,
      article:                    '',
      jurisdiction:               jurisdictionAutoDerived,
      title:                      bullet.text.trim(),
      description:                '',
      applicable:                 true,
      applicabilityJustification: '',
      linkedControlIds:           [],
      linkedDocumentIds:          [],
      linkedSupplierIds:          [],
      triggeringCustomerIds:      [savedRecord.id],
      responsiblePerson:          null,
      reviewDate:                 null,
      status:                     'Active',
      notes:                      '',
      createdAt:                  now,
      updatedAt:                  now,
      changeLog:                  [],
    };

    try {
      await writeOrgFile(org.id, `legal-register/${fileId}.json`, newEntry);
    } catch {
      updateBullet(index, { pendingAction: null, error: t.s12CreateError });
      return false;
    }

    updateBullet(index, { state: 'covered', matchedEntry: newEntry, pendingAction: null, recentAction: 'created', error: null });
    schedulePillClear(index);
    await reloadLegalRegisterEntries();
    return true;
  };

  // Bulk variants. Each runs the per-bullet action in series. If any individual
  // operation fails, the loop halts on the offending bullet; items that already
  // succeeded remain persisted (no rollback).
  const runBulkLink = async () => {
    setBulkPending(true);
    const targets = classifiedSuggestions
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => c.state === 'linkable');
    for (const { i } of targets) {
      const ok = await linkCustomerToEntry(i);
      if (!ok) break;
    }
    setBulkPending(false);
  };

  const runBulkCreate = async () => {
    setBulkPending(true);
    const targets = classifiedSuggestions
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => c.state === 'missing');
    for (const { i } of targets) {
      const ok = await createEntryFromBullet(i);
      if (!ok) break;
    }
    setBulkPending(false);
  };

  const requestBulkLink = () => {
    if (!savedRecord) return;
    const linkableCount = classifiedSuggestions.filter((c) => c.state === 'linkable').length;
    if (linkableCount === 0) return;
    setConfirmState({
      message: t.s12LinkAllConfirm.replace('{n}', String(linkableCount)),
      confirmLabel: t.s12LinkConfirmLabel,
      onConfirm: () => { setConfirmState(null); runBulkLink(); },
    });
  };

  const requestBulkCreate = () => {
    if (!savedRecord) return;
    const missingCount = classifiedSuggestions.filter((c) => c.state === 'missing').length;
    if (missingCount === 0) return;
    setConfirmState({
      message: t.s12CreateAllConfirm.replace('{n}', String(missingCount)),
      confirmLabel: t.s12CreateConfirmLabel,
      onConfirm: () => { setConfirmState(null); runBulkCreate(); },
    });
  };

  // True while any per-bullet action is in flight; combined with bulkPending it
  // gates the Regenerate button and per-bullet/bulk buttons against races with
  // an in-progress write.
  const anyPendingAction = classifiedSuggestions.some((c) => c.pendingAction !== null);
  const sectionLocked = bulkPending || anyPendingAction;

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t.errorRequired;
    const sErrs = form.sites.map((s) => {
      const e = {};
      if (!s.siteName || !s.siteName.trim()) e.siteName = true;
      if (!s.country) e.country = true;
      return e;
    });
    setSiteErrors(sErrs);
    if (sErrs.some((e) => Object.keys(e).length > 0)) errs.sites = t.errorSiteRequired;
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    if (!fileId || !ref) return;
    setSaveState('saving');
    const now = new Date().toISOString();
    try {
      const payload = {
        id:                          fileId,
        orgId:                       org.id,
        ref,
        name:                        form.name.trim(),
        legalName:                   form.legalName.trim() || null,
        sector:                      form.sector || null,
        customerType:                form.customerType,
        servicesProvided:            form.servicesProvided,
        sites:                       form.sites.map((s) => ({
          siteName:  s.siteName.trim(),
          city:      s.city.trim() || null,
          country:   s.country,
          subregion: s.subregion || null,
          units:     Array.isArray(s.units) ? s.units : [],
        })),
        processesPersonalData:       form.processesPersonalData,
        dataCategories:              form.dataCategories,
        dataStorageLocations:        form.dataStorageLocations,
        gdprRole:                    form.gdprRole,
        incidentNotificationSlaHours: form.incidentNotificationSlaHours === '' ? null : (Number(form.incidentNotificationSlaHours) || null),
        rtoHours:                    form.rtoHours === '' ? null : (Number(form.rtoHours) || null),
        rpoHours:                    form.rpoHours === '' ? null : (Number(form.rpoHours) || null),
        availabilitySla:             form.availabilitySla.trim() || null,
        rightToAudit:                form.rightToAudit,
        requiredCertifications:      form.requiredCertifications,
        dpaSigned:                   form.dpaSigned,
        dpaEvidenceId:               form.dpaSigned ? (form.dpaEvidenceId || null) : null,
        ndaSigned:                   form.ndaSigned,
        ndaEvidenceId:               form.ndaSigned ? (form.ndaEvidenceId || null) : null,
        securityAddendumSigned:      form.securityAddendumSigned,
        securityAddendumEvidenceId:  form.securityAddendumSigned ? (form.securityAddendumEvidenceId || null) : null,
        criticality:                 form.criticality,
        technicalContact:            form.technicalContact || null,
        legalContact:                form.legalContact || null,
        incidentNotificationContact: form.incidentNotificationContact || null,
        status:                      form.status,
        nextReviewDate:              form.nextReviewDate || null,
        relationshipStartDate:       form.relationshipStartDate || null,
        contractEndDate:             form.contractEndDate || null,
        notes:                       form.notes.trim(),
        createdAt:                   savedRecord?.createdAt   || now,
        updatedAt:                   now,
      };
      payload.changeLog = savedRecord ? appendChangeLog(savedRecord, payload, org.consultantPersonId || 'system') : [];
      await writeOrgFile(org.id, `customers/${fileId}.json`, payload);
      setSavedRecord(payload);
      if (currentMode !== 'edit') setCurrentMode('edit');
      onSave(fileId);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
      setSaveErrorOpen(true);
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  const handleDiscardChanges = () => {
    setSaveErrorOpen(false);
    if (savedRecord) {
      setForm(customerToForm(savedRecord));
      hasChangedRef.current = false;
      setErrors({});
      setSiteErrors([]);
    }
    onCancel();
  };
  const handleRetrySave = () => {
    setSaveErrorOpen(false);
    handleSave();
  };

  useEffect(() => {
    if (saveRequestTick > 0) handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  const handleDelete = async () => {
    try {
      await deleteOrgFile(org.id, `customers/${fileId}.json`);
      onSave(null);
    } catch {
      setDeleteError(t.errorDelete);
      setConfirmState(null);
    }
  };

  const disabled = saveState === 'saving' || isView;

  const btnPrimary = {
    ...BTN_PRIMARY, ...FONT_BUTTON,
    background: saveState === 'saving' ? TEXT_MUTED : saveState === 'ok' ? BRAND_PRIMARY_DARK : BTN_PRIMARY.background,
    border: 'none', borderRadius: 8,
    padding: '0.55rem 1.25rem', cursor: disabled ? 'default' : 'pointer',
  };
  const btnSecondary = {
    ...BTN_SECONDARY, ...FONT_BUTTON,
    borderRadius: 8,
    padding: '0.55rem 1.25rem', cursor: disabled ? 'default' : 'pointer',
  };

  return (
    <div style={{ background: BG_SURFACE, borderRadius: 12, border: `1px solid ${BORDER_DEFAULT}`, padding: '2rem' }}>
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel}
      />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ ...FONT_SECTION_TITLE, color: TEXT_HEADING, margin: 0 }}>
          {isView ? t.titleView : (currentMode === 'edit' ? t.titleEdit : t.titleCreate)}
          {ref && (
            <span style={{ ...FONT_LABEL, color: TEXT_MUTED, marginLeft: '0.75rem' }}>
              {ref}
            </span>
          )}
          {!ref && !isView && currentMode !== 'edit' && (
            <span style={{ ...FONT_SMALL, color: TEXT_DISABLED, marginLeft: '0.5rem' }}>
              {t.generating}
            </span>
          )}
        </h2>
        {!isView && (
          <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: '0.35rem 0 0' }}>
            {t.requiredLegend}
          </p>
        )}
      </div>

      {/* ── Information banner (above the first section card) ──────────────── */}
      <div style={{
        ...FONT_LABEL,
        background: STATUS_INFO.bg, color: STATUS_INFO.text,
        borderRadius: 8, padding: '0.75rem 1rem',
        marginBottom: '1.5rem',
        border: `1px solid ${STATUS_INFO.bg}`,
      }}>
        {t.banner}
      </div>

      <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {/* ── Section 1: Identification ──────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s1}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fRef}</label>
            <input
              value={ref || ''}
              readOnly
              style={{ ...inputSt(false), background: BG_CONTENT, color: TEXT_MUTED, cursor: 'default' }}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fName} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <input value={form.name} onChange={hic('name')} disabled={disabled} style={inputSt(!!errors.name)} />
            <ErrMsg msg={errors.name} />
          </div>
          <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
            <label style={labelSt}>
              {t.fLegalName}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span>
            </label>
            <input value={form.legalName} onChange={hic('legalName')} disabled={disabled} style={inputSt(false)} />
          </div>
        </div>
      </div>

      {/* ── Section 2: Nature of the relationship ──────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s2}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fSector}</label>
            <select value={form.sector} onChange={hic('sector')} disabled={disabled} style={selectSt}>
              <option value="">—</option>
              {SECTORS.map((s) => (
                <option key={s.value} value={s.value}>{lang === 'es' ? s.nameEs : s.nameEn}</option>
              ))}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fCustomerType}</label>
            <select value={form.customerType} onChange={hic('customerType')} disabled={disabled} style={selectSt}>
              {CUSTOMER_TYPES.map((v) => <option key={v} value={v}>{t.customerTypeLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
            <label style={labelSt}>{t.fServicesProvided}</label>
            <TagInput
              tags={form.servicesProvided}
              onAdd={(v) => sf('servicesProvided')([...form.servicesProvided, v])}
              onRemove={(v) => sf('servicesProvided')(form.servicesProvided.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => sf('servicesProvided')(form.servicesProvided.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.servicePlaceholder}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* ── Section 3: Customer sites ──────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: `1px solid ${BORDER_DEFAULT}` }}>
          <h3 style={{ ...FONT_BODY_STRONG, color: TEXT_HEADING, margin: 0 }}>{t.s3}</h3>
          <span style={{ ...FONT_TINY, color: TEXT_MUTED, letterSpacing: '0.04em' }}>{sitesCounter}</span>
        </div>

        {/* Derived-jurisdictions banner / empty state */}
        {form.sites.length === 0 ? (
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, fontStyle: 'italic', margin: '0 0 1rem' }}>
            {t.emptySites}
          </p>
        ) : (
          <div style={{
            ...FONT_LABEL,
            background: STATUS_INFO.bg, color: STATUS_INFO.text,
            borderRadius: 6, padding: '0.55rem 0.85rem',
            marginBottom: '1rem',
          }}>
            <strong>{t.jurisdictionsDerived}</strong>
            {derivedJurisdictions.labels.length > 0 ? derivedJurisdictions.labels.join(' · ') : '—'}
          </div>
        )}

        {errors.sites && <ErrMsg msg={errors.sites} />}

        {/* Site sub-cards */}
        {form.sites.map((s, idx) => {
          const siteErr = siteErrors[idx] || {};
          const isUsOrCa = s.country === 'US' || s.country === 'CA';
          const subList = SUBREGIONS[s.country] || [];
          return (
            <div key={idx} style={{
              background: BG_SHELL,
              border: `1px solid ${BORDER_DEFAULT}`,
              borderRadius: 8,
              padding: '1rem 1.25rem',
              marginBottom: '0.75rem',
              position: 'relative',
            }}>
              {/* Remove button */}
              {!isView && (
                <button
                  type="button"
                  onClick={() => requestRemoveSite(idx)}
                  aria-label={t.removeSite}
                  title={t.removeSite}
                  disabled={disabled}
                  style={{
                    position: 'absolute', top: 6, right: 8,
                    background: 'transparent', border: 'none',
                    color: TEXT_MUTED, fontSize: '1.1rem', lineHeight: 1,
                    cursor: disabled ? 'not-allowed' : 'pointer', padding: '0.15rem 0.35rem',
                  }}
                >×</button>
              )}
              <div style={grid2}>
                <div style={fieldWrap}>
                  <label style={labelSt}>{t.fSiteName} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                  <input
                    value={s.siteName}
                    onChange={(e) => updateSite(idx, { siteName: e.target.value })}
                    disabled={disabled}
                    style={inputSt(!!siteErr.siteName)}
                  />
                </div>
                <div style={fieldWrap}>
                  <label style={labelSt}>
                    {t.fCity}{' '}
                    <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>{t.optional}</span>
                  </label>
                  <input
                    value={s.city}
                    onChange={(e) => updateSite(idx, { city: e.target.value })}
                    disabled={disabled}
                    style={inputSt(false)}
                  />
                </div>
                <div style={fieldWrap}>
                  <label style={labelSt}>{t.fCountry} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                  <select
                    value={s.country}
                    onChange={(e) => updateSite(idx, { country: e.target.value, subregion: '' })}
                    disabled={disabled}
                    style={{ ...selectSt, border: `1px solid ${siteErr.country ? FIELD_ERROR_BORDER : BORDER_DEFAULT}` }}
                  >
                    <option value="">—</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{lang === 'es' ? c.nameEs : c.nameEn}</option>
                    ))}
                  </select>
                </div>
                <div style={fieldWrap}>
                  <label style={labelSt}>
                    {t.fSubregion}{' '}
                    {!isUsOrCa && (
                      <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                        {t.freeText}
                      </span>
                    )}
                  </label>
                  {isUsOrCa && subList.length > 0 ? (
                    <select
                      value={s.subregion}
                      onChange={(e) => updateSite(idx, { subregion: e.target.value })}
                      disabled={disabled}
                      style={selectSt}
                    >
                      <option value="">—</option>
                      {subList.map((r) => (
                        <option key={r.code} value={r.code}>{lang === 'es' ? r.nameEs : r.nameEn}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={s.subregion}
                      onChange={(e) => updateSite(idx, { subregion: e.target.value })}
                      disabled={disabled}
                      style={inputSt(false)}
                    />
                  )}
                </div>
                <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
                  <label style={labelSt}>{t.fUnits}</label>
                  <TagInput
                    tags={s.units}
                    onAdd={(v) => updateSite(idx, { units: [...(s.units || []), v] })}
                    onRemove={(v) => updateSite(idx, { units: (s.units || []).filter((x) => x !== v) })}
                    onUpdate={(oldValue, newValue) => updateSite(idx, { units: (s.units || []).map((x) => x === oldValue ? newValue : x) })}
                    placeholder={t.unitsPlaceholder}
                    disabled={disabled}
                  />
                </div>
              </div>
            </div>
          );
        })}

        {/* Add site button */}
        {!isView && (
          <button
            type="button"
            onClick={addSite}
            disabled={disabled}
            style={{
              ...FONT_LABEL,
              width: '100%',
              padding: '0.55rem 1rem',
              background: 'transparent',
              border: `1px dashed ${BORDER_DEFAULT}`,
              borderRadius: 8,
              color: TEXT_MUTED,
              cursor: disabled ? 'not-allowed' : 'pointer',
              letterSpacing: '0.04em',
            }}
          >
            {t.addSite}
          </button>
        )}
      </div>

      {/* ── Section 4: Customer data you process ──────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s4}</h3>
        <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox" id="cus-ppd"
            checked={form.processesPersonalData}
            onChange={(e) => sf('processesPersonalData')(e.target.checked)}
            disabled={disabled}
            style={{ width: 16, height: 16, flexShrink: 0 }}
          />
          <label htmlFor="cus-ppd" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
            {t.fProcessesPersonalData}
          </label>
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fDataCategories}</label>
          <TagInput
            tags={form.dataCategories}
            onAdd={(v) => sf('dataCategories')([...form.dataCategories, v])}
            onRemove={(v) => sf('dataCategories')(form.dataCategories.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('dataCategories')(form.dataCategories.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.dataCategoriesPlaceholder + ' (' + DATA_CATEGORIES.join(', ') + ')'}
            disabled={disabled}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fDataStorageLocations}</label>
          <TagInput
            tags={form.dataStorageLocations}
            onAdd={(v) => sf('dataStorageLocations')([...form.dataStorageLocations, v])}
            onRemove={(v) => sf('dataStorageLocations')(form.dataStorageLocations.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('dataStorageLocations')(form.dataStorageLocations.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.storageLocationsPlaceholder}
            disabled={disabled}
          />
        </div>
      </div>

      {/* ── Section 5: GDPR role ──────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s5}</h3>
        <div style={fieldWrap}>
          <label style={labelSt}>
            {t.fGdprRole}
            <InfoBadge text={t.ttGdprRole} />
          </label>
          <select value={form.gdprRole} onChange={hic('gdprRole')} disabled={disabled} style={{ ...selectSt, maxWidth: 320 }}>
            {GDPR_ROLES.map((v) => <option key={v} value={v}>{t.gdprRoleLabels[v]}</option>)}
          </select>
        </div>
      </div>

      {/* ── Section 6: Contractual commitments ─────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s6}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fIncidentSla}</label>
            <input
              type="number" min="0"
              value={form.incidentNotificationSlaHours}
              onChange={hic('incidentNotificationSlaHours')}
              disabled={disabled}
              style={inputSt(false)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fRto}</label>
            <input
              type="number" min="0"
              value={form.rtoHours}
              onChange={hic('rtoHours')}
              disabled={disabled}
              style={inputSt(false)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fRpo}</label>
            <input
              type="number" min="0"
              value={form.rpoHours}
              onChange={hic('rpoHours')}
              disabled={disabled}
              style={inputSt(false)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fAvailabilitySla}</label>
            <input
              value={form.availabilitySla}
              onChange={hic('availabilitySla')}
              disabled={disabled}
              style={inputSt(false)}
            />
          </div>
        </div>
        <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox" id="cus-audit"
            checked={form.rightToAudit}
            onChange={(e) => sf('rightToAudit')(e.target.checked)}
            disabled={disabled}
            style={{ width: 16, height: 16, flexShrink: 0 }}
          />
          <label htmlFor="cus-audit" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
            {t.fRightToAudit}
          </label>
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fRequiredCertifications}</label>
          <TagInput
            tags={form.requiredCertifications}
            onAdd={(v) => sf('requiredCertifications')([...form.requiredCertifications, v])}
            onRemove={(v) => sf('requiredCertifications')(form.requiredCertifications.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('requiredCertifications')(form.requiredCertifications.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.certificationsPlaceholder}
            disabled={disabled}
          />
        </div>
      </div>

      {/* ── Section 7: Signed agreements ───────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s7}</h3>
        {[
          { signedField: 'dpaSigned',              evField: 'dpaEvidenceId',              checkboxId: 'cus-dpa',  label: t.fDpaSigned,              evLabel: t.fDpaEvidence },
          { signedField: 'ndaSigned',              evField: 'ndaEvidenceId',              checkboxId: 'cus-nda',  label: t.fNdaSigned,              evLabel: t.fNdaEvidence },
          { signedField: 'securityAddendumSigned', evField: 'securityAddendumEvidenceId', checkboxId: 'cus-sec',  label: t.fSecurityAddendumSigned, evLabel: t.fSecurityAddendumEvidence },
        ].map((row) => (
          <div key={row.checkboxId} style={{ marginBottom: '0.85rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: form[row.signedField] ? '0.5rem' : 0 }}>
              <input
                type="checkbox" id={row.checkboxId}
                checked={form[row.signedField]}
                onChange={(e) => sf(row.signedField)(e.target.checked)}
                disabled={disabled}
                style={{ width: 16, height: 16, flexShrink: 0 }}
              />
              <label htmlFor={row.checkboxId} style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
                {row.label}
              </label>
            </div>
            {form[row.signedField] && (
              <div style={{ marginLeft: '1.5rem' }}>
                <label style={labelSt}>{row.evLabel}</label>
                <EvidenceSelector
                  orgId={org.id}
                  lang={lang}
                  value={form[row.evField]}
                  onChange={(val) => sf(row.evField)(val)}
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Section 8: Criticality ─────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s8}</h3>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fCriticality}</label>
          <select value={form.criticality} onChange={hic('criticality')} disabled={disabled} style={{ ...selectSt, maxWidth: 240 }}>
            {CRITICALITY_LEVELS.map((v) => <option key={v} value={v}>{t.criticalityLabels[v]}</option>)}
          </select>
        </div>
      </div>

      {/* ── Section 9: Customer contacts ───────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s9}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fTechnicalContact}</label>
            <PeopleSelector orgId={org.id} lang={lang} value={form.technicalContact} onChange={sf('technicalContact')} disabled={disabled} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fLegalContact}</label>
            <PeopleSelector orgId={org.id} lang={lang} value={form.legalContact} onChange={sf('legalContact')} disabled={disabled} />
          </div>
          <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
            <label style={labelSt}>{t.fIncidentNotificationContact}</label>
            <PeopleSelector orgId={org.id} lang={lang} value={form.incidentNotificationContact} onChange={sf('incidentNotificationContact')} disabled={disabled} />
          </div>
        </div>
      </div>

      {/* ── Section 10: Lifecycle ──────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s10}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fStatus}</label>
            <select value={form.status} onChange={hic('status')} disabled={disabled} style={selectSt}>
              {CUSTOMER_STATUSES.map((v) => <option key={v} value={v}>{t.statusLabels[v]}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fNextReviewDate}</label>
            <input type="date" value={form.nextReviewDate} onChange={hic('nextReviewDate')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fRelationshipStart}</label>
            <input type="date" value={form.relationshipStartDate} onChange={hic('relationshipStartDate')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fContractEnd}</label>
            <input type="date" value={form.contractEndDate} onChange={hic('contractEndDate')} disabled={disabled} style={inputSt(false)} />
          </div>
        </div>
      </div>

      {/* ── Section 11: Notes ──────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s11}</h3>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fNotes}</label>
          <textarea value={form.notes} onChange={hic('notes')} disabled={disabled} style={textareaSt(false)} />
        </div>
      </div>

      {/* ── Section 12: AI legal suggestions ───────────────────────────────── */}
      {!isView && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={sectionHeading}>{t.s12}</h3>

          {/* Generate button + messages */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <AISuggestButton
              onClick={handleGenerateSuggestions}
              loading={aiSuggesting}
              disabled={!org.aiProcessingConsent || sanitizedAiPayload.sitesJurisdictions.length === 0 || sectionLocked}
              lang={lang}
            />
            {!org.aiProcessingConsent && (
              <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.s12ConsentMsg}</span>
            )}
            {org.aiProcessingConsent && sanitizedAiPayload.sitesJurisdictions.length === 0 && (
              <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.s12NoSitesMsg}</span>
            )}
          </div>

          {/* Error */}
          {aiError && (
            <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 0.75rem' }}>{aiError}</p>
          )}

          {/* Unsaved-customer warning banner */}
          {aiBullets.length > 0 && !savedRecord && (
            <div style={{
              ...FONT_LABEL,
              background: STATUS_WARN.bg, color: STATUS_WARN.text,
              border: `1px solid ${STATUS_WARN.bg}`,
              borderRadius: 6, padding: '0.55rem 0.85rem',
              marginBottom: '0.75rem',
            }}>
              {t.s12NotSavedWarn}
            </div>
          )}

          {/* Classified bullets list */}
          {aiBullets.length >= 1 && !aiFallback && (() => {
            const coveredCount  = classifiedSuggestions.filter((c) => c.state === 'covered').length;
            const linkableCount = classifiedSuggestions.filter((c) => c.state === 'linkable').length;
            const missingCount  = classifiedSuggestions.filter((c) => c.state === 'missing').length;
            const dotSt = (color) => ({
              display: 'inline-block',
              width: 8, height: 8, borderRadius: '50%',
              background: color, marginRight: '0.35rem',
              verticalAlign: 'middle',
            });
            const headerRowSt = {
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem',
            };
            const countsLineSt = { ...FONT_SMALL, color: TEXT_MUTED, display: 'flex', alignItems: 'center', flexWrap: 'wrap' };
            const refLinkBaseSt = {
              ...FONT_SMALL,
              color: TEXT_DEFAULT,
              textDecoration: onNavigate ? 'underline' : 'none',
              cursor: onNavigate ? 'pointer' : 'default',
              background: 'transparent', border: 'none', padding: 0,
            };

            return (
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={headerRowSt}>
                  <div style={{ ...FONT_TINY, color: TEXT_MUTED, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {t.s12SuggestionsHeader}
                  </div>
                </div>
                <div style={countsLineSt}>
                  <span style={dotSt(STATUS_GOOD.text)} />
                  <span>{t.s12CountCovered.replace('{n}', String(coveredCount))}</span>
                  <span>{t.s12CountsSeparator}</span>
                  <span style={dotSt(STATUS_WARN.text)} />
                  <span>{t.s12CountLinkable.replace('{n}', String(linkableCount))}</span>
                  <span>{t.s12CountsSeparator}</span>
                  <span style={dotSt(STATUS_BAD.text)} />
                  <span>{t.s12CountMissing.replace('{n}', String(missingCount))}</span>
                </div>

                <div style={{ marginTop: '0.6rem' }}>
                  {classifiedSuggestions.map((c, i) => {
                    const isCovered  = c.state === 'covered';
                    const isLinkable = c.state === 'linkable';
                    const isMissing  = c.state === 'missing';
                    const tone = isCovered ? STATUS_GOOD : isLinkable ? STATUS_WARN : STATUS_BAD;
                    const glyph = isCovered ? '✓' : isLinkable ? '!' : '+';

                    const metaText = isCovered
                      ? t.s12StateCovered.replace('{ref}',  (c.matchedEntry && c.matchedEntry.ref) || '')
                      : isLinkable
                        ? t.s12StateLinkable.replace('{ref}', (c.matchedEntry && c.matchedEntry.ref) || '')
                        : t.s12StateMissing;

                    const refPart = (isCovered || isLinkable) && c.matchedEntry && c.matchedEntry.ref ? c.matchedEntry.ref : '';
                    const metaWithoutRef = refPart && metaText.endsWith(refPart)
                      ? metaText.slice(0, metaText.length - refPart.length)
                      : metaText;

                    const pendingHere = c.pendingAction !== null;
                    const actionDisabled = !savedRecord || sectionLocked || pendingHere;
                    const disabledTooltip = !savedRecord ? t.s12TooltipNotSaved : undefined;

                    const actionBtnSt = (variant) => ({
                      ...(variant === 'primary' ? BTN_PRIMARY : BTN_SECONDARY),
                      ...FONT_SMALL,
                      border: variant === 'primary' ? 'none' : BTN_SECONDARY.border,
                      borderRadius: 6,
                      padding: '0.35rem 0.7rem',
                      cursor: actionDisabled ? 'not-allowed' : 'pointer',
                      opacity: actionDisabled ? 0.55 : 1,
                      whiteSpace: 'nowrap',
                    });

                    const pillSt = {
                      ...FONT_SMALL,
                      display: 'inline-block',
                      background: STATUS_GOOD.bg, color: STATUS_GOOD.text,
                      borderRadius: 999,
                      padding: '0.15rem 0.55rem',
                      whiteSpace: 'nowrap',
                    };

                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
                          padding: '0.55rem 0',
                          borderBottom: i < classifiedSuggestions.length - 1 ? `0.5px solid ${BORDER_DEFAULT}` : 'none',
                        }}
                      >
                        <span style={{
                          flexShrink: 0,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 22, height: 22, borderRadius: 6,
                          background: tone.bg, color: tone.text,
                          ...FONT_BODY_STRONG,
                          fontSize: '0.85rem',
                        }}>
                          {glyph}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ ...FONT_BODY, color: TEXT_DEFAULT }}>{c.text}</div>
                          <div style={{ ...FONT_SMALL, color: TEXT_MUTED, marginTop: '0.15rem' }}>
                            <span>{metaWithoutRef}</span>
                            {refPart && (
                              onNavigate ? (
                                <button
                                  type="button"
                                  onClick={() => onNavigate('legal-register', null)}
                                  style={refLinkBaseSt}
                                >
                                  {refPart}
                                </button>
                              ) : (
                                <span style={{ ...FONT_SMALL, color: TEXT_DEFAULT }}>{refPart}</span>
                              )
                            )}
                          </div>
                          {c.error && (
                            <div style={{ ...FONT_SMALL, color: ERROR_TEXT, marginTop: '0.2rem' }}>
                              {c.error}
                            </div>
                          )}
                        </div>
                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          {c.recentAction === 'linked' && (
                            <span style={pillSt}>{t.s12PillLinked}</span>
                          )}
                          {c.recentAction === 'created' && (
                            <span style={pillSt}>{t.s12PillCreated}</span>
                          )}
                          {c.recentAction === null && isLinkable && (
                            <button
                              type="button"
                              onClick={() => linkCustomerToEntry(i)}
                              disabled={actionDisabled}
                              title={disabledTooltip}
                              style={actionBtnSt('secondary')}
                            >
                              {c.pendingAction === 'linking' ? t.generating : t.s12LinkBtn}
                            </button>
                          )}
                          {c.recentAction === null && isMissing && (
                            <button
                              type="button"
                              onClick={() => createEntryFromBullet(i)}
                              disabled={actionDisabled}
                              title={disabledTooltip}
                              style={actionBtnSt('primary')}
                            >
                              {c.pendingAction === 'creating' ? t.generating : t.s12CreateBtn}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Bulk footer */}
                {(() => {
                  if (!savedRecord) return null;
                  if (linkableCount === 0 && missingCount === 0) return null;
                  const bulkBtnSt = (variant, count) => ({
                    ...(variant === 'primary' ? BTN_PRIMARY : BTN_SECONDARY),
                    ...FONT_SMALL,
                    border: variant === 'primary' ? 'none' : BTN_SECONDARY.border,
                    borderRadius: 8,
                    padding: '0.45rem 0.9rem',
                    cursor: (sectionLocked || count === 0) ? 'not-allowed' : 'pointer',
                    opacity: (sectionLocked || count === 0) ? 0.55 : 1,
                    whiteSpace: 'nowrap',
                  });
                  return (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      flexWrap: 'wrap', gap: '0.6rem',
                      marginTop: '0.85rem', paddingTop: '0.75rem',
                      borderTop: `1px solid ${BORDER_DEFAULT}`,
                    }}>
                      <div style={{ ...FONT_SMALL, color: TEXT_MUTED }}>
                        {t.s12BulkSummary
                          .replace('{n}', String(missingCount))
                          .replace('{m}', String(linkableCount))}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {linkableCount > 0 && (
                          <button
                            type="button"
                            onClick={requestBulkLink}
                            disabled={sectionLocked}
                            style={bulkBtnSt('secondary', linkableCount)}
                          >
                            {t.s12LinkAll.replace('{n}', String(linkableCount))}
                          </button>
                        )}
                        {missingCount > 0 && (
                          <button
                            type="button"
                            onClick={requestBulkCreate}
                            disabled={sectionLocked}
                            style={bulkBtnSt('primary', missingCount)}
                          >
                            {t.s12CreateAll.replace('{n}', String(missingCount))}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {/* Fallback (AI returned shape B — single-sentence "no obligations") */}
          {aiBullets.length === 0 && aiFallback && (
            <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>{aiFallback}</p>
          )}

          {/* Disclaimer */}
          <p style={{ ...FONT_TINY, color: TEXT_DISABLED, fontStyle: 'italic', margin: '0.5rem 0 0' }}>
            {t.s12Disclaimer}
          </p>
        </div>
      )}

      </fieldset>

      {/* ── Sticky action bar ─────────────────────────────────────────────── */}
      {!isView && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          marginLeft: '-2rem',
          marginRight: '-2rem',
          marginBottom: '-2rem',
          marginTop: '1.5rem',
          padding: '0.85rem 2rem',
          background: BG_SURFACE,
          borderTop: `1px solid ${BORDER_DEFAULT}`,
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          zIndex: 10,
        }}>
          {saveState === 'error' && (
            <div style={{ ...FONT_LABEL, background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`, borderRadius: 6, padding: '0.35rem 0.75rem', marginBottom: '0.5rem' }}>
              {t.errorSave}
            </div>
          )}
          {deleteError && (
            <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
              {deleteError}
            </p>
          )}
          {Object.keys(errors).length > 0 && (
            <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
              {errors.sites || t.errorRequired}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              {currentMode === 'edit' && (
                <button
                  onClick={() => setConfirmState({ message: t.btnConfirmDelete, confirmLabel: t.btnDelete, onConfirm: handleDelete })}
                  disabled={disabled}
                  style={{ ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, borderRadius: 8, padding: '0.5rem 1rem', cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                  {t.btnDelete}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {dirty && (
                <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>{t.unsavedLabel}</span>
              )}
              <button onClick={onCancel} disabled={disabled} style={btnSecondary}>{t.btnCancel}</button>
              <button onClick={handleSave} disabled={disabled} style={btnPrimary}>
                {saveState === 'saving' ? t.saving : saveState === 'ok' ? t.saveOk : t.btnSave}
              </button>
            </div>
          </div>
        </div>
      )}
      <SaveErrorDialog
        open={saveErrorOpen}
        lang={lang}
        onRetry={handleRetrySave}
        onDiscard={handleDiscardChanges}
      />
    </div>
  );
}
